<template>
  <v-container>
    <v-progress-linear
      v-if="loading"
      indeterminate
      top
      color="primary"
      style="position: fixed; top: 64px; left: 0; z-index: 3;"
    />
    <v-row justify="space-between">
      <v-col
        cols="12" sm="6" md="8" lg="9"
        class="d-flex align-center title text-uppercase primary--text"
      >
        Layouts
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-btn @click="openTemplateEditor({ mode: 'create' })" block color="primary">
          {{ $t('templates.create.title') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row justify="end">
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-select
          ref="input-type"
          v-model="type"
          :items="typeOptions"
          @change="getData()"
          :placeholder="$t('templates.fields.type')"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          ref="input-search"
          v-model="search"
          @blur="getData()"
          :placeholder="$t('actions.search')"
          append-icon="search"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col v-if="!layouts.length" cols="12">
        <div class="text-center pa-12 my-12">
          <v-img
            :src="require('@/assets/svg/box_empty.svg')"
            height="150"
            max-height="150px"
            aspect-ratio="1"
            contain
          />
          <div class="grey--text">
            {{ $t('common.no_results_found') }}
          </div>
        </div>
      </v-col>
      <v-col
        v-for="(item, index) in layouts"
        :key="index"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <TemplateCard
          ref="template-card"
          :template="item"
          @show="showTemplate(item.id)"
          @edit="editTemplate(item.id)"
          @remove="deleteTemplate(item.id)"
          show
          edit
          remove
        />
      </v-col>
      <v-col cols="12">
        <CustomPagination
          :pagination="computedPagination"
          @change="getData()"
        />
      </v-col>
    </v-row>
    <TemplateEditor ref="template-editor" is-layout />
    <TemplateTranslations ref="template-translations" />
    <TemplateDesignEditor v-if="template && template.type === 'email'" ref="template-design-editor" />
    <GrapesDesignEditor v-else ref="template-design-editor" />
    <TemplateGallery ref="template-gallery" />
    <TemplateDetails ref="template-details" />
    <TemplateRemoveDialog ref="template-remove-dialog" />
    <ImageGallery />
    <ImageOwner />
  </v-container>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import templatesMixin from '@/mixins/templates'
import TemplateCard from '@/components/Templates/TemplateCard'
import TemplateEditor from '@/components/Templates/TemplateEditor.vue'
import TemplateTranslations from '@/components/Templates/TemplateTranslations.vue'
import GrapesDesignEditor from '@/components/Templates/GrapesDesignEditor.vue'
import TemplateDesignEditor from '@/components/Templates/TemplateDesignEditor.vue'
import TemplateDetails from '@/components/Templates/TemplateDetails.vue'
import TemplateGallery from '@/components/Templates/TemplateGallery.vue'
import TemplateRemoveDialog from '@/components/Templates/TemplateRemoveDialog.vue'
import ImageGallery from '@/components/Images/Gallery.vue'
import ImageOwner from '@/components/Images/Owner.vue'
import CustomPagination from '@/components/Common/CustomPagination/Index'

export default {
  name: 'Templates',
  layout: 'Dashboard',
  components: {
    TemplateCard,
    TemplateEditor,
    TemplateTranslations,
    GrapesDesignEditor,
    TemplateDesignEditor,
    TemplateGallery,
    TemplateDetails,
    TemplateRemoveDialog,
    ImageGallery,
    ImageOwner,
    CustomPagination
  },
  mixins: [templatesMixin],
  data () {
    return {
      loading: false,
      search: null,
      type: 'email',
      templateEvent: null,
      typeOptions: [
        { text: 'Email', value: 'email' },
        { text: 'Landing', value: 'landing' },
        { text: 'PDF', value: 'pdf' },
        { text: 'Thank you page', value: 'thank_you_page' }
      ],
      templateEventOptions: [],
      pagination: {
        page: 1,
        itemsLength: 0,
        itemsPerPage: 12,
        itemsPerPageOptions: [
          { value: 4, text: 4 },
          { value: 8, text: 8 },
          { value: 12, text: 12 },
          { value: 24, text: 24 }
        ],
        sortByOptions: [
          { value: 'name', text: 'Nombre' },
          { value: 'created_at', text: 'Fecha de creación' },
          { value: 'updated_at', text: 'Fecha de actualización' }
        ],
        sortBy: 'name',
        sortDesc: true
      }
    }
  },
  computed: {
    ...mapGetters({
      template: 'templates/template',
      layouts: 'templates/layouts',
      totalLayouts: 'templates/totalLayouts',
      updateRequired: 'templates/updateRequired'
    }),
    computedPagination () {
      const { pagination, totalLayouts } = this
      pagination.itemsLength = totalLayouts
      return pagination
    }
  },
  watch: {
    updateRequired (newVal) {
      this.getData()
    }
  },
  async created () {
    this.loading = true
    this.getData()
  },
  destroyed () {
    this.resetStateTemplates()
  },
  methods: {
    ...mapActions({
      resetStateTemplates: 'templates/resetState',
      getLayouts: 'templates/getLayouts',
      getTemplate: 'templates/getTemplate'
    }),
    ...mapMutations({
      setDeteledTemplateId: 'templates/setDeteledTemplateId',
      openTemplateGallery: 'templates/openTemplateGallery',
      openTemplateDetails: 'templates/openTemplateDetails',
      openTemplateEditor: 'templates/openTemplateEditor',
      openTemplateDesignEditor: 'templates/openTemplateDesignEditor',
      openTemplateRemoveDialog: 'templates/openTemplateRemoveDialog'
    }),
    async getData () {
      const { search, type, computedPagination } = this
      this.loading = true
      await this.getLayouts({
        type,
        search,
        page: computedPagination.page,
        rowsPerPage: computedPagination.itemsPerPage,
        sortBy: computedPagination.sortBy,
        descending: computedPagination.sortDesc
      })
      this.loading = false
    },
    async showTemplate (id) {
      await this.getTemplate({ id })
      this.openTemplateDetails()
    },
    async editTemplate (id) {
      await this.getTemplate({ id })
      this.openTemplateEditor({ mode: 'edit' })
    },
    deleteTemplate (id) {
      this.setDeteledTemplateId(id)
      this.openTemplateRemoveDialog()
    }
  }

}
</script>
